import { P2GBox } from '@parcel2go/core-ui';
import { useCallback, useEffect, useState } from 'react';
import {
  CustomerServiceTag,
  QuoteApiModel,
  QuoteOptionApiModel,
  QuoteProviderAccountApiModel,
  QuoteProviderApiModel
} from '~/lib/api/smartsend/types';
import Loading from '../../../../../components/loading/Loading';
import { QuoteLayout } from './layout/QuoteLayout';
import {
  CurrentSelectionType,
  DefaultExcludedServiceTags,
  DefaultQuoteRequestType,
  FavouriteIncludeServiceTags
} from './utils/CurrentSelectionType';
import useProviderName from '../../../hooks/use-provider-name';
import { QuoteLayoutHeader } from './layout/QuoteLayoutHeader';
import { QuoteTagType } from './utils/QuoteTagType';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { PopulateQuoteTags } from './utils/PopulateQuoteTags';

interface props {
  isXs: boolean;
  close: () => void;
  onBack: () => void;
  onSave?: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  onUpdate?: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  getQuotes: (request: DefaultQuoteRequestType) => Promise<QuoteApiModel[]>;
  currentSelection: CurrentSelectionType;
  estimatedValue?: number;
  quoteProviders: QuoteProviderApiModel[];
  loggingSections: string[];
  updateServiceTags: (add: CustomerServiceTag[], remove: CustomerServiceTag[]) => void;
  toggleSpecificTag: (tag: CustomerServiceTag) => void;
  selectedProviderAccount: QuoteProviderAccountApiModel;
  tags: CustomerServiceTag[];
  onSwitchAccount: () => void;
}

const QuotePicker: React.FC<props> = (props) => {
  const providerName = useProviderName();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [quotes, setQuotes] = useState<QuoteApiModel[]>([]);

  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [quoteTagType, setQuoteTagType] = useState<QuoteTagType>('Default');
  const isMulti = props.quoteProviders.length > 1;

  const makeQuoteApiCall = useCallback(async () => {
    // Service Tags
    let exc: string[] = [];
    let inc: string[] = [];

    switch (quoteTagType) {
      // Show All
      case 'Unfiltered':
        inc = [];
        exc = [];
        break;

      // Limit to those with the Favourite Tag
      case 'Favourite':
        inc = FavouriteIncludeServiceTags;
        exc = [];
        break;

      // Hide all services with Exclude tag
      case 'Default':
        inc = [];
        exc = DefaultExcludedServiceTags;
        break;
    }

    const quoteResponse = await props.getQuotes({
      provider: props.selectedProviderAccount.Provider,
      accountId: props.selectedProviderAccount.Id,
      includeTags: inc,
      excludeTags: exc,
      recipient: props.currentSelection.recipient,
      sender: props.currentSelection.sender
    });

    return quoteResponse;
  }, [props, quoteTagType]);

  const fetchQuotesCallback = useCallback(async () => {
    try {
      return await makeQuoteApiCall();
    } catch (error) {
      if (['TimeoutError', 'AbortError'].includes(error?.name)) {
        console.log('Bulk Quote Timeout');
        return makeQuoteApiCall();
      }
      throw error;
    }
  }, [makeQuoteApiCall]);

  useEffect(() => {
    const load = async () => {
      setLoadingQuotes(true);
      const quotes = await fetchQuotesCallback();
      if (quotes) {
        setQuotes((quotes || []).map(PopulateQuoteTags));
      }
      setLoadingQuotes(false);
    };

    if (props.selectedProviderAccount?.Id) {
      load();
    } else {
      setQuotes(null);
      setLoadingQuotes(false);
    }
  }, [props.selectedProviderAccount?.Id, quoteTagType, fetchQuotesCallback]);

  return (
    <QuoteLayoutHeader
      isXs={props.isXs}
      title={
        loadingQuotes
          ? props.isXs
            ? t('LOADING_QUOTES_TITLE', { defaultValue: 'Loading...' })
            : t('LOADING_QUOTES_ALTERNATIVE_TITLE', {
                defaultValue: 'Loading {{values}} Services...',
                values: providerName(props.selectedProviderAccount.Provider)
              })
          : props.isXs
            ? t('SELECT_SERVICES_TITLE', {
                defaultValue: '{{provider}} Services',
                provider: providerName(props.selectedProviderAccount.Provider)
              })
            : t('SELECT_SERVICES_ALTERNATIVE_TITLE', {
                defaultValue: 'Select {{provider}} Services',
                provider: providerName(props.selectedProviderAccount.Provider)
              })
      }
      onBack={props.onBack}
      currentSelection={props.currentSelection}
      multiAccount={isMulti}
      selectedServiceProvider={props.selectedProviderAccount}
    >
      {loadingQuotes ? (
        <P2GBox pt={2} pb={2} textAlign="center">
          <Loading />
        </P2GBox>
      ) : (
        <P2GBox pt={2}>
          <QuoteLayout
            isXs={props.isXs}
            toggleSpecificTag={props.toggleSpecificTag}
            multiAccount={isMulti}
            loggingSections={props.loggingSections}
            selectedServiceProvider={props.selectedProviderAccount}
            onSave={props.onSave}
            quotes={quotes}
            currentSelection={props.currentSelection}
            quoteTagType={quoteTagType}
            setQuoteTagType={setQuoteTagType}
            updateServiceTags={props.updateServiceTags}
            serviceTags={props.tags}
            estimatedValue={props.estimatedValue}
            onBackClick={props.onBack}
            onSwitchAccount={props.onSwitchAccount}
          />
        </P2GBox>
      )}
    </QuoteLayoutHeader>
  );
};

export default QuotePicker;
